/**
 * Reset-password landing page. The provider link is exchanged automatically
 * (PKCE detectSessionInUrl), so a valid link means a recovery session exists
 * and useAuth() reports 'authenticated'. Without a session we honestly say
 * the link is invalid or expired — no fake form.
 */
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banner, Button, PasswordField, StatusCard } from '@/components/ui'
import { PATHS } from '@/app/router/paths'
import { SessionLoadingScreen, portalHomeForRole } from '@/app/router/RequireAuth'
import { isCloudConfigured } from '@/lib/supabase/client'
import { isAppError, toAppError } from '@/lib/supabase/errors'
import type { AppError } from '@/types'
import { useAuth } from '../useAuth'
import { PASSWORD_POLICY_HINT, validatePassword, validatePasswordConfirm } from '../validation'
import { AuthLayout, AuthLegalFooter, NotConfiguredBanner, RustWordmark } from './AuthLayout'

export default function ResetPasswordPage() {
  const { status, role, updatePassword } = useAuth()
  const navigate = useNavigate()
  const configured = isCloudConfigured()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({})
  const [formError, setFormError] = useState<AppError | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  if (!configured) {
    return (
      <AuthLayout backTo={PATHS.auth.forgotPassword} backLabel="Terug">
        <RustWordmark width={140} />
        <div className="auth-card">
          <NotConfiguredBanner />
        </div>
        <AuthLegalFooter />
      </AuthLayout>
    )
  }

  if (status === 'loading') {
    return <SessionLoadingScreen label="Je herstel-link wordt gecontroleerd…" />
  }

  if (status === 'unauthenticated') {
    return (
      <AuthLayout backTo={PATHS.auth.forgotPassword} backLabel="Terug">
        <RustWordmark width={140} />
        <h1 className="auth-headline" style={{ fontSize: 26 }}>
          Link niet geldig
        </h1>
        <div className="auth-card">
          <StatusCard
            status="warning"
            title="Deze herstel-link is verlopen of al gebruikt"
            body="Vraag een nieuwe link aan om je wachtwoord opnieuw in te stellen."
            action={
              <Button variant="primary" fullWidth onClick={() => navigate(PATHS.auth.forgotPassword)}>
                Nieuwe link aanvragen
              </Button>
            }
          />
        </div>
        <AuthLegalFooter />
      </AuthLayout>
    )
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) return
    const errors: { password?: string; confirm?: string } = {}
    const passwordError = validatePassword(password)
    if (passwordError) errors.password = passwordError
    const confirmError = validatePasswordConfirm(password, confirm)
    if (confirmError) errors.confirm = confirmError
    setFieldErrors(errors)
    if (errors.password || errors.confirm) return

    setSubmitting(true)
    setFormError(null)
    try {
      await updatePassword(password)
      setDone(true)
    } catch (error) {
      setFormError(isAppError(error) ? error : toAppError(error, 'auth'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <RustWordmark width={140} />
      <h1 className="auth-headline" style={{ fontSize: 26 }}>
        Nieuw wachtwoord kiezen
      </h1>
      {done ? (
        <div className="auth-card">
          <StatusCard
            status="success"
            title="Je wachtwoord is aangepast"
            body="Je bent ingelogd met je nieuwe wachtwoord."
            action={
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(role ? portalHomeForRole(role) : PATHS.root, { replace: true })}
              >
                Doorgaan
              </Button>
            }
          />
        </div>
      ) : (
        <form className="auth-card" onSubmit={onSubmit} noValidate>
          {formError && (
            <Banner tone="error">
              {formError.userMessage}
            </Banner>
          )}
          <PasswordField
            label="Nieuw wachtwoord"
            value={password}
            onChange={setPassword}
            error={fieldErrors.password}
            hint={PASSWORD_POLICY_HINT}
            required
            autoComplete="new-password"
            disabled={submitting}
          />
          <PasswordField
            label="Herhaal nieuw wachtwoord"
            value={confirm}
            onChange={setConfirm}
            error={fieldErrors.confirm}
            required
            autoComplete="new-password"
            disabled={submitting}
          />
          <div className="auth-form-footer">
            <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting} disabled={submitting}>
              Wachtwoord opslaan
            </Button>
          </div>
        </form>
      )}
      <AuthLegalFooter />
    </AuthLayout>
  )
}
